import {MATH_TYPE, PRIMITIVE_MATH_TYPES, XAU_MATH_TYPES} from 'common/report/constants';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import type {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {PropertyType, ResourceType} from 'common/report/constants';
import {DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {FilterOperator} from 'common/widgets/property-filter-menu/models/types';
import {MultiAttribution} from 'common/widgets/multi-attribution-menu/types';
import {ChartPlotStyle} from 'common/report/insights/util/types';
import {RollingWindowSize} from 'common/util';
import {RangeSelection} from 'common/widgets/step-range-selector/types';

import {EventItem} from '../query-block/events-menu/types';
import {PropertyCombineMode} from '../query-block/properties-menu/types';
import {InlineCohortSegment, SegmentValue} from '../query-block/segment-menu/types';
import {PropertySectionItem} from '../query-block/properties-menu/item-menu-types';
import {SelectorTypeAggregationLabel} from '../types/shared-types';

export type IncludeCustomProperties = `none` | `persisted-only` | `persisted-non-behavioral-only` | `all`;

export interface QueryEntrySectionAttrs {
  'add-entry-label': string;
  'add-entry-button-label': string;
  'add-entry-button-tooltip-text': string;
  'add-entry-menu-width': number;
  'allow-cohort-filter': boolean;
  'autoactivate-placeholders': boolean;
  'can-rename-step': boolean;
  'enable-enhanced-list-filters': boolean;
  'group-event-behavior': GroupEventBehaviors;
  'data-group-id': string;
  'data-groups-enabled': boolean;
  'date-range': string;
  'date-unit': RollingWindowSize;
  'disable-map-to-lookup-table': boolean;
  'disabled-items': Obj;
  'property-combine-mode': PropertyCombineMode;
  'disable-add-event-filters': boolean;
  'disable-clone-entry': boolean;
  'disable-delete-entry': boolean;
  'disable-first-time-event-filters': boolean;
  'enable-workspace-filter-restrictions': boolean;
  'disable-add-grouped-event-reason': string;
  disabled: boolean;
  'disable-add-entry': boolean;
  'disable-add-entry-reason': string;
  'entry-type': EntryType;
  'event-aggregator-functions': MATH_TYPE[];
  'row-label-type-for-exclusion-steps': RowLabelType;
  'property-aggregator-functions': MATH_TYPE[];
  entries: QuerySuperEntry[];
  'min-entries': number;
  'empty-placeholder': QuerySuperEntry;
  'exclude-autotrack-events': boolean;
  'exclude-custom-events': boolean;
  'exclude-dimension-properties': boolean;
  'include-custom-properties': IncludeCustomProperties;
  'is-blocked-people': boolean;
  autofocus: boolean;
  'max-data-history-days': number;
  'metrics-mode': boolean;
  'behavior-mode': boolean;
  'num-exclusion-steps': number;
  'plot-style': ChartPlotStyle;
  'project-has-cohorts': boolean;
  'project-has-people': boolean;
  'project-id': number;
  'user-id': number;
  'row-draggable': boolean;
  'row-drop-disabled': boolean;
  'undraggable-rows': number[];
  readonly: boolean;
  'restrict-top-events-only': boolean;
  'row-types': RowTypeOption[];
  'row-index-toggleable': boolean;
  'row-index-toggle-icon': string;
  'row-index': number;
  'row-label-prefix': string;
  'row-label-prefix-icon': string;
  'row-label-type': RowLabelType;
  'row-limit': number;
  'show-all-events': boolean;
  'show-any-event': boolean;
  'hide-edit-controls': boolean;
  'show-sessions': boolean;
  'show-top-events': boolean;
  'show-special-date-property': boolean;
  'hide-special-date-property-unit': boolean;
  'show-special-dataset-property': boolean;
  'support-b2b-cohorts': boolean;
  'inline-cohorts-override': Obj;
  'time-display-context': DisplayContext;
  'track-name': string;
  'track-properties': UnknownObj;
  'workspace-id': number;
  'project-as-of-date': string;
  'event-names': string[];
  'other-group-by-options': string;
  'other-filter-options': string;
  'hide-events': boolean;
  'can-customize-numeric-buckets': boolean;
  'can-use-multi-attribution': boolean;
  /** when rendering the section as a styled container, includes a title in the header area */
  'section-title': string;
  'enable-sticky-header': boolean;
}

export enum SelectorTypeDefinitions {
  Behavior = `behavior`,
  EventLaunch = `event_launch`,
  FlowsEvent = `flows_event`,
  GroupedEvent = `grouped_event`,
  Filter = `filter`,
  Segment = `segment`,
  CohortGroup = `cohort_group`,
  PropertyExpansion = `property_expansion`,
}

/** TODO(pablo): remove union with ResourceType and flatten this list */
export type SelectorType = ResourceType | SelectorTypeDefinitions;

export const SelectorTypes: SelectorType[] = [
  ResourceType.Cohort,
  ResourceType.Event,
  ResourceType.User,
  SelectorTypeDefinitions.Behavior,
  SelectorTypeDefinitions.EventLaunch,
  SelectorTypeDefinitions.FlowsEvent,
  SelectorTypeDefinitions.GroupedEvent,
];

export type RowTypeComplexOption = {
  value: SelectorType;
  placeholderEntry?: Partial<QuerySuperEntry>;
  includeDateRange?: boolean;
};

/** A RowType represents what kind of entries can be added to the list */
export type RowTypeOption = SelectorType | RowTypeComplexOption;

/** TODO(pablo): remove type based helpers once we can remove `event`
 * for representing all value selections in an entry
 */
export function isEventBasedType(type: SelectorType): boolean {
  const propertyTypes = [
    ResourceType.Event,
    SelectorTypeDefinitions.Behavior,
    SelectorTypeDefinitions.EventLaunch,
    SelectorTypeDefinitions.FlowsEvent,
  ] as SelectorType[];

  return propertyTypes.includes(type || ResourceType.Event);
}

export function isUserBasedType(entry: QuerySuperEntry): entry is QueryUserEntry {
  const propertyTypes = [ResourceType.User] as SelectorType[];

  return propertyTypes.includes(entry.type);
}

export function isPropertySelectorType(type: SelectorType): type is QueryPropertyEntry['type'] {
  const propertyTypes = [
    SelectorTypeDefinitions.Filter,
    SelectorTypeDefinitions.Segment,
    SelectorTypeDefinitions.PropertyExpansion,
  ] as SelectorType[];
  return propertyTypes.includes(type);
}

export enum FiltersOperator {
  And = `and`,
  Or = `or`,
  OrAll = `or_all`,
  AndAny = `and_any`,
  Then = `then`, // [shawn] Special-case for support of funnels queries within the cohorts-query-builder
}

export const FiltersOperatorDisplayLabels: {[x in FiltersOperator]?: string} = {
  [FiltersOperator.And]: `and`,
  [FiltersOperator.AndAny]: `and any`,
  [FiltersOperator.Or]: `or`,
  [FiltersOperator.OrAll]: `or all`,
  [FiltersOperator.Then]: `then`,
};

export interface SavedCohortItem {
  resourceType?: ResourceType.Cohort;
  label: string;
  renamed?: string;
  // "value" is the cohort's id
  value: number;
  data_group_id?: string;
  negated?: boolean;
}

export interface SpecialCohortItem extends Omit<SavedCohortItem, 'value'> {
  // string for pre-defined cohorts ($all_users)
  value: string;
}

interface InlineCohortItem {
  resourceType?: ResourceType.Cohort;
  label: string;
  value: InlineCohortSegment;
  negated?: boolean;
}
export type CohortItem = SavedCohortItem | InlineCohortItem | SpecialCohortItem;

export function isInlineCohortItem(item: CohortItem): item is InlineCohortItem {
  return item.value && (item as InlineCohortItem).value.cohort !== undefined;
}

export function isSavedCohortItem(item: CohortItem): item is SavedCohortItem {
  return item.value && typeof (item as SavedCohortItem).value === `number`;
}

export interface BaseEntry {
  dataGroupId?: string;

  /** Set of filter clauses to be applied to the event */
  filters: PropertyFilter[];

  /** Set of behavioral filters */
  behavioralFilters?: PropertyFilter[];
  behavioralFiltersOperator?: FiltersOperator;

  /** Boolean operator to join filter clauses */
  filtersOperator?: FiltersOperator;

  /** Position of the event if this event specifies exclusion */
  excludeRange?: UnknownObj | {from: number; to: number};

  /** Specifies if exclusion event range will include all collection events */
  excludeAll?: boolean;

  /** Numeric property applied on event */
  property?: PropertySectionItem;

  /** Specific mathematical aggregation performed on event and its properties */
  aggregationOperator?: MATH_TYPE;

  /** Specific mathematical aggregation performed on a per-user basis */
  aggregationOperatorPerUser?: MATH_TYPE;

  rolling?: {rollingWindowSize: number};

  cumulative?: boolean;

  /** if set, the event index label is displayed in toggled state */
  entryIndexLabelToggle?: boolean;

  /** if set, the event value cannot be updated */
  readOnlyEvent?: boolean;

  /** for funnels and flows */
  forward?: number;
  reverse?: number;

  /** for internal use, stores committed values for an entry in stage mode */
  persistedEntry?: QuerySuperEntry;

  /** For internal use, the unique key of this entry row for use with snabbdom */
  entryKey?: number;

  /** internal non-serializable prefix label for the main entry selector */
  prefixLabel?: string;
}

// TODO: Rename event -> item or cohort/event/filter/segment
export interface QueryEventEntry extends BaseEntry {
  type: ResourceType.Event;
  event: EventItem;
  dateRange?: Partial<DateRange>;
  /** Custom bucketing for HISTOGRAM aggregationOperator  */
  customBucket?: Bucket;
  multiAttribution?: MultiAttribution;
}

export function isEventEntry(entry: QuerySuperEntry): entry is QueryEventEntry {
  return entry.type === ResourceType.Event;
}

export interface FlowsQueryEventEntry extends BaseEntry {
  type: SelectorTypeDefinitions.FlowsEvent;
  event: EventItem;
  funnelStep?: boolean;
}

interface QueryUserEntry extends BaseEntry {
  type: ResourceType.User;
  event: EventItem;
}

export interface QueryCohortEntry extends BaseEntry {
  type: ResourceType.Cohort;
  event: CohortItem;
}

export interface CohortGroupEntry extends BaseEntry {
  groupingOperator?: FiltersOperator.And | FiltersOperator.Or;
  type: SelectorTypeDefinitions.CohortGroup;
  event: CohortItem;
}

export interface QueryBehaviorEntry extends BaseEntry {
  /** @todo [pablo] query-entry-section does not have great typing and support for `Behavior` entries so we pass it as events in some places */
  type: SelectorTypeDefinitions.Behavior | ResourceType.Event;
  event: EventItem;
  dateRange?: Partial<DateRange>;
  entryFilter?: Partial<PropertyFilter>;
  aggregationOperator?: MATH_TYPE;
  filtersOperator?: FiltersOperator;
}

export interface QueryEventLaunch extends BaseEntry {
  type: SelectorTypeDefinitions.EventLaunch;
  event: EventItem;
  dateRange?: Partial<DateRange>;
}

/**
 * An entry which contains multiple child entries.
 *
 * TODO (later): shouldn't extend BaseEntry, but that would require more
 * refactoring of query-entry-section.
 */
export interface QueryGroupedEntry extends BaseEntry {
  type: SelectorTypeDefinitions.GroupedEvent;
  event: QueryEventEntry[];
}

export function isGroupedEntry(entry: QuerySuperEntry): entry is QueryGroupedEntry {
  return entry.type === SelectorTypeDefinitions.GroupedEvent && Array.isArray(entry.event);
}

export interface OperatorOverride {
  value: FilterOperator;
  label?: string;
}

export interface QueryFilterEntry extends BaseEntry {
  clearable?: boolean; // TODO remove once we remove customize segments v1
  filterOnly?: boolean;
  hideListQuantifier?: boolean;
  filtersOperator?: FiltersOperator | null;
  type: SelectorTypeDefinitions.Filter;
  event: Partial<PropertyFilter>;
  quickDelete?: boolean;
  quickRename?: boolean;
  operatorOverrides?: OperatorOverride[] | null;
  suppressTracking?: boolean;
}

export function isFilterEntry(entry: QuerySuperEntry): entry is QueryFilterEntry {
  return entry && entry.type === SelectorTypeDefinitions.Filter;
}

export interface QuerySegmentEntry extends BaseEntry {
  type: SelectorTypeDefinitions.Segment;
  event: SegmentValue;
  customBucket?: Bucket;
}

export function isSegmentEntry(entry: QuerySuperEntry): entry is QuerySegmentEntry {
  return entry.type === SelectorTypeDefinitions.Segment;
}

/** Union of entry types which may contain a customBucket. */
export type QueryBucketableEntry = QueryEventEntry | QuerySegmentEntry;

/**
 * QB-internal representation of Filter and Breakdown entry types.
 *
 * This is distinct from event/user property aggregations as a QueryMetricEntry.
 */
export type QueryPropertyEntry = QueryFilterEntry | QuerySegmentEntry;
export function isPropertyEntry(entry: QuerySuperEntry): entry is QueryPropertyEntry {
  return isPropertySelectorType(entry.type);
}

export function isObjectEntry(entry: QuerySuperEntry) {
  return (entry as QueryFilterEntry)?.event?.joinPropertyType === PropertyType.Object;
}

/** A single entry representing one cohort, event, property, etc. */
export type QueryMetricEntry =
  | QueryEventEntry
  | FlowsQueryEventEntry
  | QueryUserEntry
  | QueryCohortEntry
  | QueryBehaviorEntry
  | QueryEventLaunch;

/** All entry types that can be passed to the query builder. Safe for app code to depend on. */
export type QueryEntry = QueryMetricEntry | QueryGroupedEntry | CohortGroupEntry;

/**
 * Query builder internal: all possible types of query entry. App code shouldn't depend on this.
 */
export type QuerySuperEntry = QueryEntry | QueryMetricEntry | QueryPropertyEntry | QueryGroupedEntry;

export type EntryItem = QuerySuperEntry['event'];

export enum InsightsCountType {
  UniqueUsers = `Unique Users`,
  TotalEvents = `Total Events`,
  TotalSessions = `Total Sessions`,
  FrequencyPerUser = `Frequency per User`,
  PropertyValues = `Aggregate Property`,
  PropertyValuePerUser = `Aggregate Property per User`,
}

export enum RetentionCountType {
  Unique = `Unique`,
  RetentionRate = `Retention Rate`,
}

export enum FunnelsCountType {
  Unique = `unique`,
  General = `general`,
  Session = `session`,
  ConversionRate = `conversion_rate`,
  TimeToConvert = `time_to_convert`,
}

export enum CalculationTypes {
  Rolling = `rolling`,
  XAU = `xau`,
  Cumulative = `cumulative`,
}
export interface Calculation {
  calculationType: CalculationTypes | null;
  rollingWindowSize?: number;
  xauOperator?: XAU_MATH_TYPES;
}

export type AdvancedMenuValue = Calculation | RangeSelection | number;

export type CountType = InsightsCountType | RetentionCountType | FunnelsCountType;

export interface CountBy {
  countType: CountType;
  aggregationOperator?: MATH_TYPE;
  aggregationOperatorPerUser?: MATH_TYPE;
  property?: PropertySectionItem;
  advancedMenuValue?: AdvancedMenuValue;
}

export enum Action {
  Add = `add`,
  AddFilter = `addFilter`,
  ApplyProperty = `applyProperty`,
  Clone = `clone`,
  Drag = `drag`,
  Edit = `edit`,
  Group = `group`,
  Ungroup = `ungroup`,
  EditFilter = `editFilter`,
  Remove = `remove`,
  RemoveFilter = `removeFilter`,
  Select = `select`,
  SelectDateRange = `selectDateRange`,
  EditFlowSteps = `editFlowSteps`,
  ToggleValueComparison = `toggleValueComparison`,
}

export enum RowLabelType {
  None = `none`,
  Alpha = `alpha`,
  Numeric = `numeric`,
}

export enum EntryType {
  Event = `event`,
  ExclusionEvent = `exclusion-event`,
}

export const SelectorTypesLabels: {[key in SelectorType]?: string} = {
  // event based types
  [ResourceType.Event]: `event`,
  [SelectorTypeDefinitions.FlowsEvent]: `event`,
  [SelectorTypeDefinitions.Behavior]: `behavior`,
  [SelectorTypeDefinitions.EventLaunch]: `event`,
  [SelectorTypeDefinitions.GroupedEvent]: `event`,
  [ResourceType.Cohort]: `cohort`,
  [ResourceType.User]: `user profile`,
};

const EMPTY_EVENT: EventItem = Object.freeze({
  id: null,
  custom: false,
  label: ``,
  value: null,
});

export const EMPTY_EVENT_ENTRY = Object.freeze({
  aggregationOperator: PRIMITIVE_MATH_TYPES.TOTAL,
  aggregationOperatorPerUser: null,
  event: EMPTY_EVENT,
  filters: [],
  filtersOperator: FiltersOperator.And,
  property: null,
  type: ResourceType.Event,
}) as QueryEventEntry;

export const EMPTY_EXCLUSION_EVENT_ENTRY: QueryEventEntry = Object.freeze({
  ...EMPTY_EVENT_ENTRY,
  excludeAll: true,
  excludeRange: {},
});

export type SelectorTypeAggregation = {
  /** specifies if the aggregation acts on a property */
  forProperty?: boolean;

  /** Defines across-users aggregations */
  operator?: MATH_TYPE;

  /** Defines per-user aggregations */
  perUserOperator?: MATH_TYPE;

  label: SelectorTypeAggregationLabel;
  description?: SelectorTypeAggregationLabel;
  subLabel?: SelectorTypeAggregationLabel;
  shortLabel?: SelectorTypeAggregationLabel;

  /** defines aggregations to be displayed in a nested menu */
  subSections?: SelectorTypeAggregation[];

  /** 2nd level aggregation definitions for a 1st pass aggregation, e.g. distribution on a Total per User by Distribution query */
  subAggregations?: SelectorTypeAggregation[];
};

/** When using Add grouped event, how to add the new row. */
export enum GroupEventBehaviors {
  /** Grouped events are disabled. `Add grouped event` is not allowed. */
  None = `none`,
  /** Adding a grouped event should append it to the current entries list. */
  Append = `append`,
  /** Adding a grouped event should create a new group at the current entry. */
  Nest = `nest`,
}

/** When a row-label-prefix is passed, how to display it and handle interactions. */
export enum RowPrefixBehaviors {
  /** Don't show a row prefix at all. */
  None = `none`,
  /** Show the prefix on every row. */
  EveryRow = `every`,
  /** Show the prefix only on the first row. */
  FirstOnly = `first`,
}

/** Identifier for a possibly nested entry. Ex: [0,2] = first entry, third child. */
export type RowAddress = [number] | [number, number];

export enum EntryPart {
  Aggregator = `aggregator`,
  FirstTimeFilters = `firstTimeFilters`,
  BehavioralFrequencyFilters = `behavioralFrequencyFilters`,
  CustomBucketing = `customBucketing`,
  MultiAttribution = `multiAttribution`,
  Filters = `filters`,
  NumericProperty = `numericProperty`,
  SubAggregator = `subAggregator`,
  Calculation = `calculation`,
  FilterValue = `filterValue`,
  EntryFilterValue = `entryFilterValue`,
  Value = `value`,
  /** for list of objects breakdown */
  SubValue = `subValue`,
}

export type SectionOpenEntry = {index: number; part: EntryPart} | EmptyObj;

export type EntrySectionUpdateEvent<EntryType = QuerySuperEntry> = CustomEvent<{
  entries: EntryType[];
  updatedEntry: EntryType;
  updatedEntryIndex: number;
  queryReason?: QueryReason;
}>;

export type UpdateStatus = {
  dispatchedUpdate: boolean;
};

export enum DROP_POSITION {
  ABOVE,
  BELOW,
  ON,
  HEADER,
}
